import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useLogin } from "@/lib/api";
import { logger } from "@/lib/logger";

// Constant-length JSX expression keeps prettier output stable for any rendered name.
const PROJECT_NAME = "Conversational Asset Composer";

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Set by the signup page when the account is usable straight away, so the
  // first thing a new tester sees is "you're in", not a bare sign-in form.
  const justRegistered = searchParams.get("registered") === "1";
  const loginMutation = useLogin();
  const [email, setEmail] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    logger.debug("[login] submitting for", email);
    try {
      const result = await loginMutation.mutateAsync({ email, password, rememberMe });
      logger.debug("[login] success → redirecting to /home", result);
      navigate("/composer");
    } catch (err) {
      const status = (err as { status?: number } | undefined)?.status;
      const detail = (err as { body?: { detail?: string } } | undefined)?.body?.detail;
      if (status === 403 && detail === "email_not_verified") {
        navigate(`/verify-email?email=${encodeURIComponent(email)}`);
        return;
      }
      logger.debug("[login] failed:", err);
      setError(status === 401 ? "Invalid credentials." : "Sign-in failed. Please try again.");
    }
  };

  return (
    <div className="bg-background flex min-h-screen items-center justify-center px-4 py-10">
      <div className="grid w-full max-w-6xl overflow-hidden rounded-[2rem] border border-black/5 bg-white shadow-[0_24px_80px_rgba(0,0,0,0.08)] lg:grid-cols-[1.05fr_0.95fr]">
        <div className="from-primary/7 to-background hidden bg-gradient-to-br p-10 lg:flex lg:flex-col lg:justify-center">
          <div>
            <img
              src="/brand/velux-logo.svg"
              alt="VELUX"
              className="h-9 w-auto"
              width={127}
              height={42}
            />
            <p className="text-primary mt-6 text-sm font-semibold tracking-[0.22em] uppercase">
              Sales enablement
            </p>
            <h1 className="font-heading text-foreground mt-5 max-w-md text-4xl leading-tight font-bold">
              Build daylight-focused sales assets with the speed local markets need.
            </h1>
            <p className="text-muted mt-5 max-w-xl text-base leading-7">
              Retrieve approved imagery, generate localized PPTX outputs, and keep every request
              aligned with the rules that protect the VELUX brand.
            </p>
          </div>
        </div>
        <Card className="w-full rounded-none border-0 shadow-none">
          <CardHeader className="pt-10 text-center">
            <CardTitle className="font-heading text-primary text-2xl font-bold">
              {PROJECT_NAME}
            </CardTitle>
            <p className="text-muted mx-auto max-w-sm text-sm leading-6">
              Sign in to create localized VELUX-ready sales materials from approved assets.
            </p>
          </CardHeader>
          <CardContent className="px-8 pb-10 sm:px-10">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="email" className="text-foreground text-sm font-medium">
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="name@velux.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="password" className="text-foreground text-sm font-medium">
                  Password
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-muted hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2 transition-colors"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="rememberMe"
                  checked={rememberMe}
                  onCheckedChange={(checked) => setRememberMe(checked === true)}
                />
                <label htmlFor="rememberMe" className="text-foreground text-sm">
                  Remember me for 30 days
                </label>
              </div>
              {justRegistered && !error && (
                <p className="text-muted text-sm">
                  Account created. Sign in with the password you just chose.
                </p>
              )}
              {error && <p className="text-destructive text-sm">{error}</p>}
              <Button
                type="submit"
                disabled={loginMutation.isPending}
                className="bg-accent text-accent-foreground hover:bg-accent/90 w-full"
              >
                {loginMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Signing in…
                  </>
                ) : (
                  "Sign in"
                )}
              </Button>
              <p className="text-muted text-center text-sm">
                Don&apos;t have an account?{" "}
                <Link to="/signup" className="text-accent hover:underline">
                  Sign up
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
